// apps/admin/src/components/datatable/PageListFooter.tsx
//
// Phase 8.2 audit 2026-05-23 — shared paginated-list footer that mirrors the
// DataTable.tsx footer block exactly so list pages NOT built on the full
// TanStack-driven DataTable still meet the canonical /stores standard:
// Rows-per-page Select on the left, Pagination (Total + First/Prev/numbered
// ±5/Next/Last) on the right.
//
// Why a standalone footer instead of pushing every list page onto DataTable:
// the heavyweight DataTable owns columns, sort, filter, URL sync, CSV export,
// saved views — re-wiring 13+ admin billing list pages onto it is a multi-
// phase refactor. PageListFooter is the lift-and-shift compromise: every
// existing list page can adopt it in <20 lines without touching its row
// rendering or filter UI.
//
// Reuses the same `@printway/ui` Pagination primitive that DataTable's
// footer uses (sizes 25/50/100 retained as default choices).

import type { JSX } from 'react';

import {
  Pagination as UIPagination,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@printway/ui';

import { useTranslation } from '../../i18n/useTranslation.js';

export const DEFAULT_PAGE_SIZE_CHOICES = [10, 25, 50, 100] as const;

export interface PageListFooterProps {
  /** 1-based current page. */
  page: number;
  /** Active page size. */
  pageSize: number;
  /** Total rows known to the caller (BE-reported). */
  total: number;
  /** Optional explicit total-pages override (else derived from total/pageSize). */
  totalPages?: number;
  /** Page-size choices shown in the Rows selector. Defaults to 10/25/50/100. */
  pageSizeChoices?: readonly number[];
  /** Disable inputs (loading state). */
  loading?: boolean;
  /** Page change handler. 1-based. */
  onPageChange: (page: number) => void;
  /** Page-size change handler. */
  onPageSizeChange: (size: number) => void;
  /** Test id root for the wrapper. Defaults to "page-list-footer". */
  testId?: string;
}

export function PageListFooter(props: PageListFooterProps): JSX.Element {
  const {
    page,
    pageSize,
    total,
    totalPages: explicitTotalPages,
    pageSizeChoices = DEFAULT_PAGE_SIZE_CHOICES,
    loading = false,
    onPageChange,
    onPageSizeChange,
    testId = 'page-list-footer',
  } = props;
  const { t } = useTranslation('admin');

  const computedTotalPages = Math.max(
    1,
    Math.ceil((total || 0) / Math.max(1, pageSize)),
  );
  const pageCount = explicitTotalPages ?? computedTotalPages;

  return (
    <div
      className="flex flex-wrap items-center justify-between gap-3 text-sm text-pw-text-muted"
      data-testid={testId}
    >
      <label className="flex items-center gap-2">
        <span className="text-xs">{t('datatable.rows')}</span>
        <Select
          value={String(pageSize)}
          onValueChange={(value) =>
            onPageSizeChange(Number.parseInt(value, 10))
          }
          disabled={loading}
        >
          <SelectTrigger
            aria-label={t('datatable.rowsPerPage')}
            data-testid={`${testId}-pagesize`}
          >
            <SelectValue placeholder={t('datatable.rowsPerPage')} />
          </SelectTrigger>
          <SelectContent>
            {pageSizeChoices.map((n) => (
              <SelectItem key={n} value={String(n)}>
                {n}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </label>
      <UIPagination
        currentPage={page}
        pageCount={pageCount}
        totalRows={total}
        windowSize={5}
        disabled={loading}
        onChange={onPageChange}
        testId={testId}
      />
    </div>
  );
}

export default PageListFooter;
